/**
 * SampleLightbox — Dialog-based image viewer.
 *
 * Generic: coupled only to its props (a list of images + a controlled index).
 * Renders the current image large, an optional caption, and prev/next controls
 * plus a counter when there is more than one image. Keyboard ArrowLeft /
 * ArrowRight step through; the Dialog primitive handles Esc / click-out / close.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface LightboxImage {
  url: string;
  caption?: string | null;
  alt?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: LightboxImage[];
  index: number;
  onIndexChange: (index: number) => void;
}

export function SampleLightbox({
  open,
  onOpenChange,
  images,
  index,
  onIndexChange,
}: Props) {
  const { t } = useTranslation('productDev');
  const total = images.length;
  // Clamp so a stale index (e.g. after a photo delete) never reads undefined.
  const safeIndex = total > 0 ? Math.min(Math.max(index, 0), total - 1) : 0;
  const current = images[safeIndex];
  const hasMultiple = total > 1;

  const goPrev = () => {
    if (!hasMultiple) return;
    onIndexChange(Math.max(safeIndex - 1, 0));
  };
  const goNext = () => {
    if (!hasMultiple) return;
    onIndexChange(Math.min(safeIndex + 1, total - 1));
  };

  useEffect(() => {
    if (!open || !hasMultiple) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onIndexChange(Math.max(safeIndex - 1, 0));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onIndexChange(Math.min(safeIndex + 1, total - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hasMultiple, safeIndex, total, onIndexChange]);

  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-3">
        <DialogTitle className="sr-only">
          {current.alt ?? current.caption ?? t('samples.openPhoto')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t('samples.lightbox.description')}
        </DialogDescription>

        <div className="relative flex items-center justify-center">
          <img
            src={current.url}
            alt={current.alt ?? current.caption ?? t('samples.samplePhotoAlt')}
            className="max-h-[80vh] w-auto max-w-full rounded-md object-contain"
          />

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={goPrev}
                disabled={safeIndex === 0}
                aria-label={t('samples.lightbox.prev')}
                className={cn(
                  'absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center',
                  'rounded-full bg-background/80 text-foreground shadow-md ring-1 ring-border',
                  'transition-opacity duration-200 hover:bg-background disabled:opacity-30 motion-reduce:transition-none',
                )}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={safeIndex === total - 1}
                aria-label={t('samples.lightbox.next')}
                className={cn(
                  'absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center',
                  'rounded-full bg-background/80 text-foreground shadow-md ring-1 ring-border',
                  'transition-opacity duration-200 hover:bg-background disabled:opacity-30 motion-reduce:transition-none',
                )}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {(current.caption || hasMultiple) && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">{current.caption ?? ''}</p>
            {hasMultiple && (
              <span
                className="shrink-0 text-xs tabular-nums text-muted-foreground"
                aria-live="polite"
              >
                {t('samples.lightbox.counter', {
                  current: safeIndex + 1,
                  total,
                })}
              </span>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
